import { useNavigate, useLocation } from 'react-router-dom';
import {
  Database,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  LayoutDashboard,
  Megaphone,
  Briefcase,
  GitBranch,
  Users,
  Shield,
  Globe,
  XCircle,
  UserPlus,
  Building2,
  FileText,
  ExternalLink,
  Mail,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppSidebar, SidebarSection, SidebarItem, useAuth, AppVersionBadge } from '@aaas/shared';
import { useATSRole } from '../hooks/useATSRole';

export interface AtsSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function AtsSidebar({ isOpen = true, onClose }: AtsSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const { isLeaderOrAbove } = useATSRole();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose?.();
  };

  return (
    <AppSidebar isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col min-h-full">
      {/* Standalone: Dashboard — app home, not part of any group */}
      <SidebarSection>
        <SidebarItem
          icon={<LayoutDashboard className="h-5 w-5" />}
          label={t('nav.dashboard', { defaultValue: 'Dashboard' })}
          isActive={isActive('/ats/dashboard')}
          onClick={() => handleNavigation('/ats/dashboard')}
        />
      </SidebarSection>

      {/* Recruitment Pipeline */}
      <SidebarSection title={t('nav.sectionRecruitment')}>
        <SidebarItem
          icon={<CalendarDays className="h-5 w-5" />}
          label={t('nav.calendar')}
          isActive={isActive('/ats/calendar')}
          onClick={() => handleNavigation('/ats/calendar')}
        />
        <SidebarItem
          icon={<Mail className="h-5 w-5" />}
          label={t('nav.emails')}
          isActive={isActive('/ats/emails')}
          onClick={() => handleNavigation('/ats/emails')}
        />
        <SidebarItem
          icon={<Database className="h-5 w-5" />}
          label={t('nav.pools')}
          isActive={isActive('/ats/pools')}
          onClick={() => handleNavigation('/ats/pools')}
        />
        <SidebarItem
          icon={<ClipboardList className="h-5 w-5" />}
          label={t('nav.jobs')}
          isActive={isActive('/ats/jobs')}
          onClick={() => handleNavigation('/ats/jobs')}
        />
        {isLeaderOrAbove && (
          <SidebarItem
            icon={<FileText className="h-5 w-5" />}
            label={t('nav.jobDescriptions')}
            isActive={isActive('/ats/job-descriptions')}
            onClick={() => handleNavigation('/ats/job-descriptions')}
          />
        )}
      </SidebarSection>

      {/* Candidates */}
      <SidebarSection title={t('nav.sectionCandidates')}>
        <SidebarItem
          icon={<Users className="h-5 w-5" />}
          label={t('nav.cvs')}
          isActive={isActive('/ats/cvs')}
          onClick={() => handleNavigation('/ats/cvs')}
        />
      </SidebarSection>

      {/* Configuration - only for leader/manager */}
      {isLeaderOrAbove && (
        <SidebarSection title={t('nav.sectionConfiguration')}>
          <SidebarItem
            icon={<Briefcase className="h-5 w-5" />}
            label={t('nav.positions')}
            isActive={isActive('/ats/positions')}
            onClick={() => handleNavigation('/ats/positions')}
          />
          <SidebarItem
            icon={<Megaphone className="h-5 w-5" />}
            label={t('nav.campaigns')}
            isActive={isActive('/ats/campaigns')}
            onClick={() => handleNavigation('/ats/campaigns')}
          />
          <SidebarItem
            icon={<GitBranch className="h-5 w-5" />}
            label={t('nav.pipelines')}
            isActive={isActive('/ats/pipelines')}
            onClick={() => handleNavigation('/ats/pipelines')}
          />
          <SidebarItem
            icon={<Globe className="h-5 w-5" />}
            label={t('nav.sources')}
            isActive={isActive('/ats/sources')}
            onClick={() => handleNavigation('/ats/sources')}
          />
          <SidebarItem
            icon={<XCircle className="h-5 w-5" />}
            label={t('nav.rejectionReasons')}
            isActive={isActive('/ats/rejection-reasons')}
            onClick={() => handleNavigation('/ats/rejection-reasons')}
          />
          <SidebarItem
            icon={<Users className="h-5 w-5" />}
            label={t('nav.employeeTypes')}
            isActive={isActive('/ats/employee-types')}
            onClick={() => handleNavigation('/ats/employee-types')}
          />
          <SidebarItem
            icon={<UserPlus className="h-5 w-5" />}
            label={t('nav.recruitmentTypes')}
            isActive={isActive('/ats/recruitment-types')}
            onClick={() => handleNavigation('/ats/recruitment-types')}
          />
          <SidebarItem
            icon={<Building2 className="h-5 w-5" />}
            label={t('nav.workArrangements')}
            isActive={isActive('/ats/work-arrangements')}
            onClick={() => handleNavigation('/ats/work-arrangements')}
          />
          <SidebarItem
            icon={<CalendarClock className="h-5 w-5" />}
            label={t('nav.eventTypes')}
            isActive={isActive('/ats/event-types')}
            onClick={() => handleNavigation('/ats/event-types')}
          />
          <SidebarItem
            icon={<Mail className="h-5 w-5" />}
            label={t('nav.emailTemplates')}
            isActive={isActive('/ats/email-templates')}
            onClick={() => handleNavigation('/ats/email-templates')}
          />
          <SidebarItem
            icon={<Shield className="h-5 w-5" />}
            label={t('nav.roles')}
            isActive={isActive('/ats/roles')}
            onClick={() => handleNavigation('/ats/roles')}
          />
        </SidebarSection>
      )}

      {/* Careers Site — pinned to bottom */}
      {currentWorkspace?.code && (
        <div className="mt-auto px-3 pb-2 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => {
              const careersBase = import.meta.env.VITE_CAREERS_BASE_URL || 'https://careers.aaas.chat';
              window.open(`${careersBase}/${currentWorkspace.code}`, '_blank');
              onClose?.();
            }}
            className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg w-full text-left text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <ExternalLink className="h-5 w-5 flex-shrink-0 text-gray-500" />
            <span className="flex-1 truncate">{t('nav.careersSite')}</span>
          </button>
        </div>
      )}
      <div className="px-4 pt-4 mt-2 border-t border-gray-100">
        <AppVersionBadge slug="ats" />
      </div>
      </div>
    </AppSidebar>
  );
}
